// 확신도 맞히기 — 아는 것을 늘리는 게 아니라 "내가 얼마나 아는지"를 정확히 아는 연습 (보정 훈련).
// 비교 문제: 둘 중 고르고 확신(50~99%)을 건다 → 확신 평균과 실제 정답률의 차이 = 과신.
// 구간 문제: 90% 확신하는 범위를 적는다 → 10번 중 9번 들어가야 정상 (대부분은 너무 좁게 잡는다).
// 레벨 = 비교하는 두 값이 얼마나 가까운가. 계단식 레벨이 정답률 50~80% 근처에 머물게 해 늘 '헷갈리는' 문제로 확신을 연습한다.
import { FACTS, intervalQuestion, type Fact, type FactCat } from './facts';
import { makeRng, shuffle } from './rng';

type PairCat = Exclude<FactCat, 'misc'>;

export interface CalibPair { kind: 'pair'; id: string; prompt: string; a: Fact; b: Fact; answer: 'a' | 'b'; level: number }
export interface CalibInterval { kind: 'interval'; id: string; fact: Fact; question: string }
export type CalibItem = CalibPair | CalibInterval;

export interface PairAnswer { id: string; conf: number; hit: boolean; level: number }
export interface IntervalAnswer { id: string; lo: number; hi: number; v: number; hit: boolean }

export const CALIB_PAIRS = 8;
export const CALIB_INTERVALS = 3;
export const CONF_LEVELS = [50, 60, 70, 80, 90, 99];

const PROMPT: Record<PairCat, string> = {
  year: '더 먼저 일어난 일은?',
  peak: '더 높은 산은?',
  tower: '더 높은 건물·탑은?',
  pop: '인구가 더 많은 나라는? (2024년 무렵)',
  area: '면적이 더 넓은 나라는?',
  count: '수가 더 큰 쪽은?',
};

/** 이보다 가까우면 비교 문제로 내지 않는다 — 값이 출처마다 조금씩 다른 분류는 넉넉하게 */
const MIN_RATIO: Record<Exclude<PairCat, 'year'>, number> = { peak: 1.015, tower: 1.015, count: 1.015, pop: 1.08, area: 1.08 };
const YEAR_GAP = [200, 120, 70, 45, 30, 20, 13, 8, 4, 1];         // 레벨 1~10 의 최소 연도 차
const RATIO = [5, 3, 2, 1.6, 1.35, 1.22, 1.13, 1.08, 1.04, 0];   // 레벨 1~10 의 최소 배율

/** 두 사실을 비교 문제로 낼 때의 레벨 (낼 수 없으면 null) */
export function pairLevel(a: Fact, b: Fact): number | null {
  if (a.cat !== b.cat || a.cat === 'misc') return null;
  if (a.cat === 'year') {
    const gap = Math.abs(a.v - b.v);
    return gap < 1 ? null : YEAR_GAP.findIndex((g) => gap >= g) + 1;
  }
  const r = Math.max(a.v, b.v) / Math.min(a.v, b.v);
  return r < MIN_RATIO[a.cat] ? null : RATIO.findIndex((g) => r >= g) + 1;
}

/** 정답 쪽: 연도는 더 이른 것, 나머지는 더 큰 것 */
export const pairWinner = (a: Fact, b: Fact): 'a' | 'b' => ((a.cat === 'year' ? a.v < b.v : a.v > b.v) ? 'a' : 'b');

let pairs: { a: Fact; b: Fact; level: number }[] | null = null;
export function allPairs() {
  if (!pairs) {
    pairs = [];
    for (let i = 0; i < FACTS.length; i++) {
      for (let j = i + 1; j < FACTS.length; j++) {
        const level = pairLevel(FACTS[i], FACTS[j]);
        if (level !== null) pairs.push({ a: FACTS[i], b: FACTS[j], level });
      }
    }
  }
  return pairs;
}

export function makeCalibRound(level: number, seed: number): CalibItem[] {
  const rng = makeRng(seed);
  const lv = Math.min(10, Math.max(1, level));
  const used = new Set<string>();
  const perCat = new Map<FactCat, number>();
  const out: CalibItem[] = [];
  const pool = shuffle(rng, allPairs());
  // 레벨이 꼭 맞는 쌍부터, 모자라면 한 칸씩 넓힌다. 한 판에 같은 사실은 한 번, 한 분류는 몇 개까지만
  for (let spread = 0; out.length < CALIB_PAIRS && spread < 10; spread++) {
    for (const p of pool) {
      if (out.length >= CALIB_PAIRS) break;
      if (Math.abs(p.level - lv) !== spread || used.has(p.a.id) || used.has(p.b.id)) continue;
      const n = perCat.get(p.a.cat) ?? 0;
      if (n >= (p.a.cat === 'year' ? 4 : 3)) continue;
      perCat.set(p.a.cat, n + 1);
      used.add(p.a.id).add(p.b.id);
      const [a, b] = rng() < 0.5 ? [p.a, p.b] : [p.b, p.a];
      out.push({ kind: 'pair', id: `${a.id}|${b.id}`, prompt: PROMPT[a.cat as PairCat], a, b, answer: pairWinner(a, b), level: p.level });
    }
  }
  const cats = new Set<FactCat>();
  for (const f of shuffle(rng, FACTS)) {
    if (out.length >= CALIB_PAIRS + CALIB_INTERVALS) break;
    if (used.has(f.id) || cats.has(f.cat)) continue;
    cats.add(f.cat);
    out.push({ kind: 'interval', id: f.id, fact: f, question: intervalQuestion(f) });
  }
  return out;
}

const pctNum = (x: number) => Math.round(x * 100);

/** 한 판 결과 요약 — 결과 화면에 보여 준다 */
export function calibSummary(answers: PairAnswer[], intervals: IntervalAnswer[]): string[] {
  const out: string[] = [];
  if (answers.length) {
    const conf = answers.reduce((s, a) => s + a.conf, 0) / answers.length;
    const acc = pctNum(answers.filter((a) => a.hit).length / answers.length);
    const gap = Math.round(conf - acc);
    const verdict = conf <= 55
      ? '거의 다 찍기(50%)였습니다 — 조금이라도 아는 문제는 60~70%를 걸어 보세요'
      : Math.abs(gap) < 5 ? '확신과 실제가 비슷합니다' : gap > 0 ? `${gap}%p 과신` : `${-gap}%p 과소확신 (아는 것보다 겸손)`;
    out.push(`확신 평균 ${Math.round(conf)}% · 실제 정답 ${acc}% → ${verdict}`);
    const sureWrong = answers.filter((a) => a.conf >= 99 && !a.hit).length;
    if (sureWrong) out.push(`'확실(99%)'이라고 하고 틀린 문제 ${sureWrong}개 — 99%는 100번 중 1번만 틀린다는 뜻입니다.`);
  }
  if (intervals.length) {
    const hits = intervals.filter((i) => i.hit).length;
    out.push(hits === intervals.length
      ? `90% 구간 ${hits}/${intervals.length} 적중 — 목표대로입니다.`
      : `90% 구간 ${hits}/${intervals.length} 적중 — 대부분 사람은 구간을 너무 좁게 잡습니다. 10번 중 9번 들어갈 만큼 넓히세요.`);
  }
  out.push('한 판은 문제가 적어 들쭉날쭉합니다. 누적 결과는 분석 화면에 쌓입니다.');
  return out;
}
